import {
  CalendarDays,
  ClipboardCheck,
  CircleHelp,
  CreditCard,
  Gift,
  Home,
  Landmark,
  Settings,
  TrendingUp,
  UsersRound,
  WalletCards,
  Wrench,
  Zap,
} from 'lucide-react';

import type { DashboardView } from '../../features/dashboard/dashboard.types';
import { useCurrentUser } from '../../features/users/users.api';
import { useI18n } from '../../i18n/useI18n';

const primaryLinks = [
  { href: '#inicio', icon: Home, id: 'home', label: 'navigation.home' },
  { href: '#plan', icon: TrendingUp, id: 'plan', label: 'navigation.plan' },
  { href: '#rapido', icon: Zap, id: 'quick', label: 'navigation.quick' },
  { href: '#agenda', icon: ClipboardCheck, id: 'agenda', label: 'navigation.agenda' },
  { href: '#calendario', icon: CalendarDays, id: 'calendar', label: 'navigation.calendar' },
  { href: '#herramientas', icon: Wrench, id: 'tools', label: 'navigation.tools' },
  { href: '#wallets', icon: WalletCards, id: undefined, label: 'navigation.wallets' },
] as const;

export function Sidebar({
  billingActive,
  adminActive,
  accountActive,
  banksActive,
  onAccount,
  onAdmin,
  onBanks,
  referralsActive,
  onNavigate,
  onUpgrade,
  onWallets,
  onSupport,
  supportActive,
  walletsActive,
  view,
}: {
  readonly accountActive: boolean;
  readonly adminActive: boolean;
  readonly banksActive: boolean;
  readonly billingActive: boolean;
  readonly onAccount: (tab: 'preferences' | 'referrals' | 'sharing') => void;
  readonly onAdmin: () => void;
  readonly onBanks: () => void;
  readonly onNavigate: (view: DashboardView) => void;
  readonly onUpgrade: () => void;
  readonly onWallets: () => void;
  readonly onSupport: () => void;
  readonly referralsActive: boolean;
  readonly supportActive: boolean;
  readonly walletsActive: boolean;
  readonly view: DashboardView;
}) {
  const currentUser = useCurrentUser();
  const { t } = useI18n();
  const isStaff = currentUser.data?.role !== undefined && currentUser.data.role !== 'user';
  return (
    <aside className="sidebar" aria-label={t('navigation.sidebar')}>
      <p className="sidebar-label">Finance Go</p>
      <nav aria-label={t('navigation.finance')}>
        {primaryLinks.map(({ href, icon: Icon, id, label }) => {
          const active = id === undefined ? walletsActive : id === view;
          return (
            <a
              aria-current={active ? 'page' : undefined}
              className={active ? 'sidebar-link is-active' : 'sidebar-link'}
              href={href}
              key={href}
              onClick={(event) => {
                event.preventDefault();
                if (id === undefined) onWallets();
                else onNavigate(id);
              }}
            >
              <Icon aria-hidden="true" />
              {t(label)}
            </a>
          );
        })}
      </nav>
      <p className="sidebar-label">{t('navigation.account')}</p>
      <nav aria-label={t('navigation.account')}>
        <a
          aria-current={accountActive ? 'page' : undefined}
          className={accountActive ? 'sidebar-link is-active' : 'sidebar-link'}
          href="#cuenta"
          onClick={(event) => {
            event.preventDefault();
            onAccount('sharing');
          }}
        >
          <UsersRound aria-hidden="true" /> {t('navigation.sharedAccount')}
        </a>
        <a
          aria-current={referralsActive ? 'page' : undefined}
          className={referralsActive ? 'sidebar-link is-active' : 'sidebar-link'}
          href="#referidos"
          onClick={(event) => {
            event.preventDefault();
            onAccount('referrals');
          }}
        >
          <Gift aria-hidden="true" /> {t('navigation.referrals')}
        </a>
        <a
          aria-current={banksActive ? 'page' : undefined}
          className={banksActive ? 'sidebar-link is-active' : 'sidebar-link'}
          href="#bancos"
          onClick={(event) => {
            event.preventDefault();
            onBanks();
          }}
        >
          <Landmark aria-hidden="true" /> {t('navigation.banks')}
        </a>
        <a
          aria-current={billingActive ? 'page' : undefined}
          className={billingActive ? 'sidebar-link is-active' : 'sidebar-link'}
          href="#upgrade"
          onClick={(event) => {
            event.preventDefault();
            onUpgrade();
          }}
        >
          <CreditCard aria-hidden="true" /> {t('navigation.plans')}
        </a>
        <a
          className="sidebar-link"
          href="#ajustes"
          onClick={(event) => {
            event.preventDefault();
            onAccount('preferences');
          }}
        >
          <Settings aria-hidden="true" /> {t('navigation.settings')}
        </a>
        <a
          aria-current={supportActive ? 'page' : undefined}
          className={supportActive ? 'sidebar-link is-active' : 'sidebar-link'}
          href="#soporte"
          onClick={(event) => {
            event.preventDefault();
            onSupport();
          }}
        >
          <CircleHelp aria-hidden="true" /> {t('navigation.support')}
        </a>
        {isStaff && (
          <a
            aria-current={adminActive ? 'page' : undefined}
            className={adminActive ? 'sidebar-link is-active' : 'sidebar-link'}
            href="#admin"
            onClick={(event) => {
              event.preventDefault();
              onAdmin();
            }}
          >
            <UsersRound aria-hidden="true" /> {t('navigation.admin')}
          </a>
        )}
      </nav>
    </aside>
  );
}
